/**
 * Stripe Connect integration tab for onboarding
 * Handles Stripe Connect setup interface
 */

import React, { useState } from 'react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  CreditCard, 
  ExternalLink, 
  CheckCircle, 
  AlertCircle, 
  Clock,
  Shield,
  DollarSign,
  Zap,
  ChevronRight
} from 'lucide-react';

import { useOnboarding } from '@/hooks/useOnboarding';
import { OnboardingSection, OnboardingStatus } from '@/types/onboarding';

const StripeConnectTab: React.FC = () => {
  const {
    onboardingData,
    getSectionStatus,
    markSectionComplete,
    isMarkingComplete,
  } = useOnboarding();

  const [isProcessing, setIsProcessing] = useState(false);
  const [stripeInfoExpanded, setStripeInfoExpanded] = useState(false);

  const sectionStatus = getSectionStatus(OnboardingSection.STRIPE_CONNECT);
  const stripeConnectLink = onboardingData?.stripe_connect_link;
  const hasStripeLink = stripeConnectLink && stripeConnectLink.trim() !== '';

  // Handle Stripe Connect link click
  const handleStripeConnect = () => {
    if (hasStripeLink) {
      setIsProcessing(true);
      window.open(stripeConnectLink, '_blank', 'noopener,noreferrer');
      // Reset processing state after a delay
      setTimeout(() => setIsProcessing(false), 3000);
    }
  };

  // Mark section as complete
  const handleMarkComplete = async () => {
    try {
      await markSectionComplete(OnboardingSection.STRIPE_CONNECT);
    } catch (error) {
      console.error('Error marking Stripe Connect section complete:', error);
    }
  };

  // Status badge component
  const StatusBadge = () => {
    switch (sectionStatus) {
      case OnboardingStatus.COMPLETE:
        return <Badge className="bg-green-100 text-green-800">Complete</Badge>;
      case OnboardingStatus.AWAITING_CONFIRMATION:
        return <Badge className="bg-yellow-100 text-yellow-800">Under Review</Badge>;
      case OnboardingStatus.NEEDS_ATTENTION:
        return <Badge className="bg-red-100 text-red-800">Needs Attention</Badge>;
      default:
        return <Badge variant="secondary">Not Started</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CreditCard className="h-6 w-6 text-primary" />
          <div>
            <h2 className="text-xl font-semibold">Payment Processing</h2>
            <p className="text-sm text-muted-foreground">
              Set up Stripe Connect for secure payment processing
            </p>
          </div>
        </div>
        <StatusBadge />
      </div>

      {/* Status alerts */}
      {sectionStatus === OnboardingStatus.NEEDS_ATTENTION && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            There's an issue with your Stripe Connect setup. Please contact our support team for assistance.
          </AlertDescription>
        </Alert>
      )}

      {sectionStatus === OnboardingStatus.AWAITING_CONFIRMATION && (
        <Alert>
          <Clock className="h-4 w-4" />
          <AlertDescription>
            Your Stripe Connect setup is being verified. This usually takes 1-2 business days.
          </AlertDescription>
        </Alert>
      )}

      {sectionStatus === OnboardingStatus.COMPLETE && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700">
            Your Stripe Connect account has been successfully set up and verified. You can now accept payments.
          </AlertDescription>
        </Alert>
      )}

      {/* What is Stripe Connect */}
      <Card>
        <CardHeader 
          className="cursor-pointer" 
          onClick={() => setStripeInfoExpanded(!stripeInfoExpanded)}
        >
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              What is Stripe Connect?
            </div>
            <ChevronRight 
              className={`h-4 w-4 transition-transform duration-200 ${
                stripeInfoExpanded ? 'rotate-90' : ''
              }`} 
            />
          </CardTitle>
          <CardDescription>
            Understanding how payment processing works with Pump'd
          </CardDescription>
        </CardHeader>
        {stripeInfoExpanded && (
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Stripe Connect allows you to accept payments directly into your own Stripe account while 
            using Pump'd's platform. This means you maintain full control over your funds and can 
            access detailed financial reporting.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
              <Shield className="h-5 w-5 text-blue-600 mt-1" />
              <div>
                <h4 className="font-medium text-sm text-blue-900">Secure</h4>
                <p className="text-xs text-blue-700">
                  Bank-level security with PCI compliance
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg">
              <DollarSign className="h-5 w-5 text-green-600 mt-1" />
              <div>
                <h4 className="font-medium text-sm text-green-900">Direct Deposits</h4>
                <p className="text-xs text-green-700">
                  Funds go directly to your bank account
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-lg">
              <Zap className="h-5 w-5 text-purple-600 mt-1" />
              <div>
                <h4 className="font-medium text-sm text-purple-900">Fast Setup</h4>
                <p className="text-xs text-purple-700">
                  Quick verification process
                </p>
              </div>
            </div>
          </div>
        </CardContent>
        )}
      </Card>

      {/* Stripe Connect Setup */}
      <Card>
        <CardHeader>
          <CardTitle>Set Up Your Stripe Account</CardTitle>
          <CardDescription>
            Connect your Stripe account to start accepting payments
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {hasStripeLink ? (
            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-r from-brand-red-light-1 to-brand-purple border rounded-lg">
                <h4 className="font-medium text-white mb-2">Ready to Connect</h4>
                <p className="text-sm text-white mb-4">
                  Our team has prepared a custom Stripe Connect link for your business.
                  Click the button below to complete your Stripe account setup.
                </p>
                
                <Button
                  onClick={handleStripeConnect}
                  disabled={isProcessing}
                  className="flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  {isProcessing ? 'Opening Stripe...' : 'Connect with Stripe'}
                </Button>
              </div>

              <div className="space-y-3">
                <h4 className="font-medium">What happens next:</h4>
                <div className="space-y-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs flex items-center justify-center">1</div>
                    <div className="flex-1">
                      <span>You'll be redirected to Stripe to create or connect your account</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs flex items-center justify-center">2</div>
                    <div className="flex-1">
                      <span>Provide your business and banking information to Stripe</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs flex items-center justify-center">3</div>
                    <div className="flex-1">
                      <span>Stripe will verify your information (usually takes 1-2 business days)</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs flex items-center justify-center">4</div>
                    <div className="flex-1">
                      <span>You'll be able to accept payments through Pump'd</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="font-medium text-amber-900 mb-2">Stripe Link Not Available</h4>
                <p className="text-sm text-amber-700">
                  Your custom Stripe Connect link hasn't been set up yet. Our team will provide this 
                  during your onboarding process.
                </p>
              </div>

              <div className="text-sm text-muted-foreground">
                <p className="mb-2">
                  <strong>Don't worry!</strong> This is normal if:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>You've just started the onboarding process</li>
                  <li>Our team is still preparing your account details</li>
                  <li>You're waiting for business details verification</li>
                </ul>
                <p className="mt-3">
                  If you have questions, please contact our support team at{' '}
                  <a href="mailto:support@pumpd.co.nz" className="text-primary hover:underline">
                    support@pumpd.co.nz
                  </a>
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Requirements */}
      <Card>
        <CardHeader>
          <CardTitle>Requirements for Stripe Connect</CardTitle>
          <CardDescription>
            What you'll need to complete the setup
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <h4 className="font-medium">Business Information</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Business name and type</li>
                <li>• Business address</li>
                <li>• Tax identification numbers</li>
                <li>• Director/owner information</li>
              </ul>
            </div>
            
            <div className="space-y-3">
              <h4 className="font-medium">Banking Details</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Business bank account details</li>
                <li>• Account holder verification</li>
                <li>• Banking statements (if required)</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action buttons */}
      <div className="flex items-center justify-end pt-4">
        <Button
          onClick={handleMarkComplete}
          disabled={isMarkingComplete}
          variant={sectionStatus === OnboardingStatus.COMPLETE ? "secondary" : "default"}
          className="flex items-center gap-2"
        >
          <CheckCircle className="h-4 w-4" />
          {isMarkingComplete ? 'Submitting...' : 
           sectionStatus === OnboardingStatus.COMPLETE ? 'Completed' : 'Mark as Complete'}
        </Button>
      </div>
    </div>
  );
};

export default StripeConnectTab;